import React, { type ReactElement, useMemo } from 'react';
import { faCircleDown, faCircleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { type ScaleLinear, axisLeft, scaleLinear, select } from 'd3';
import { groupBy, orderBy } from 'lodash';
import ItemWithStatsTooltip from 'feature-components/chart-tooltips/item-tooltip/item-tooltip';
import 'feature-components/charts/bubble-chart/bubble-chart.scss';
import type { Statistics } from 'feature-components/reports/item-assessment-report';
import { getCategory } from 'feature-components/reports/item-assessment-report/columns/common-columns';
import type { Sort } from 'feature-components/reports/item-assessment-report/controls/select-sort-type';
import { useCategoriesFromItems } from 'feature-components/reports/item-assessment-report/item-assessment-report.helpers';
import type { BaseArea } from 'services/assist-api/entities/area/areas';
import type { Category } from 'services/assist-api/entities/test-item/test-items';
import './item-performance-chart.scss';

type Props = {
  questions: Statistics[];
  area: BaseArea;
  minPercentageCorrect: number;
  minItemScaleScore: number;
  maxItemScaleScore: number;
  itemSorting: Sort;
  iconWidth: number;
  onClickCategory: (c: Category) => void;
};

type TestItemOrPadding =
  | Statistics
  | { id: string; categories: Statistics['categories']; isPadding: true };

type ItemsByCategory = Record<string, TestItemOrPadding[]>;

const BAND_INCREMENTS = 10;

function generateBands(
  minBand: number,
  maxBand: number
): { id: string; label: string; min: number; max: number }[] {
  return [
    {
      id: `${minBand}`,
      label: `${minBand} and below`,
      min: minBand - BAND_INCREMENTS,
      max: minBand
    },
    ...Array.from({ length: (maxBand - minBand) / BAND_INCREMENTS }, (_, i) => {
      const startRange = minBand + i * BAND_INCREMENTS;
      const endRange = startRange + BAND_INCREMENTS - 1;

      return {
        id: `${startRange}-${endRange}`,
        label: `${startRange}-${endRange}`,
        min: startRange,
        max: endRange
      };
    }),
    {
      id: `${maxBand}`,
      label: `${maxBand} and above`,
      min: maxBand,
      max: maxBand + BAND_INCREMENTS
    }
  ];
}

const options = {
  size: {
    width: 800,
    height: 400
  },
  plot: {
    margin: {
      top: 5,
      right: 20,
      bottom: 90,
      left: 80
    }
  }
};

const getPlotAreaTop = (opts: typeof options): number => opts.plot.margin.top;
const getPlotAreaBottom = (opts: typeof options): number =>
  opts.size.height - opts.plot.margin.bottom;
const getPlotAreaLeft = (opts: typeof options): number => opts.plot.margin.left + 5;
const getPlotAreaRight = (opts: typeof options): number => opts.size.width - opts.plot.margin.right;
const yAxisPosition = (opts: typeof options): number => opts.plot.margin.left;
const xAxisPosition = (opts: typeof options): number =>
  getPlotAreaBottom(opts) - opts.plot.margin.top;

function xAxis({
  node,
  categories,
  itemsByCategory,
  scaleX,
  onClickCategory
}: {
  node: SVGElement;
  categories: Category[];
  itemsByCategory: ItemsByCategory;
  scaleX: ScaleLinear<number, number, never>;
  onClickCategory: Props['onClickCategory'];
}): void {
  const height = options.plot.margin.bottom;

  const getCategoryWidth = (c: Category): number =>
    scaleX(itemsByCategory[c.id].length) - getPlotAreaLeft(options);

  const getCategoryX = (i: number): number => {
    if (i === 0) return scaleX(0);
    return scaleX(
      categories
        .slice(0, i)
        .reduce(
          (accumulator, category) => accumulator + (itemsByCategory[category.id]?.length ?? 0),
          0
        )
    );
  };
  const g = select(node);

  g.selectChildren().remove(); // clean up anything that might be left over from previous renders

  // Category Label
  const xAxisLabels = g
    .append('foreignObject')
    .attr('width', getPlotAreaRight(options) - getPlotAreaLeft(options))
    .attr('height', height)
    .attr('y', getPlotAreaTop(options))
    .attr('x', getPlotAreaLeft(options))
    .style('overflow', 'visible')
    .append('xhtml:div')
    .attr('class', 'x-axis-labels');

  xAxisLabels
    .append('div')
    .attr('class', 'category-label-holder')
    .selectAll('div.category-label')
    .data(categories)
    .join('div')
    .attr('class', 'category-label')
    .style('height', `${options.plot.margin.bottom}px`)
    .style('width', (d) => `${getCategoryWidth(d)}px`)
    .on('click', (_, c) => onClickCategory(c))
    .html((category) => {
      if (!category.alternativeName) return category.name;
      return `
                <div>
                  ${category.alternativeName}<br />
                  ${category.name}
                </div>
              `;
    });

  g.append('g')
    .attr('class', 'x-axis-grid')
    .attr('transform', `translate(0 ${-xAxisPosition(options)})`)
    .selectAll('line')
    .data(categories)
    .join('line')
    .attr('hidden', (c, i) => i === 0 || null)
    .attr('x1', (c, i) => getCategoryX(i))
    .attr('x2', (c, i) => getCategoryX(i))
    .attr('y1', getPlotAreaBottom(options))
    .attr('y2', getPlotAreaTop(options))
    .attr('stroke', 'white');
}

function yAxis({
  node,
  bands,
  scaleY
}: {
  node: SVGElement;
  bands: ReturnType<typeof generateBands>;
  scaleY: ScaleLinear<number, number, never>;
}): void {
  const yAxisGroup = select(node);

  yAxisGroup.selectChildren().remove(); // clean up anything that might be left over from previous renders

  yAxisGroup
    .append('rect')
    .attr('class', 'y-axis-bg')
    .attr('y', getPlotAreaTop(options))
    .attr('height', getPlotAreaBottom(options) - getPlotAreaTop(options))
    .attr('fill', 'white');

  yAxisGroup
    .append('g')
    .attr('class', 'scale')
    .call(
      axisLeft(scaleY)
        .tickValues(bands.map((band) => band.max - (band.max - band.min) / 2))
        .tickFormat((_, i) => bands[i].label)
        .tickSize(0)
    );

  yAxisGroup
    .append('g')
    .attr('class', 'ticks')
    .call(
      axisLeft(scaleY)
        .tickValues(bands.slice(1).map((band) => band.min))
        .tickFormat(() => '')
        .tickSize(16)
        .tickSizeOuter(32)
    );

  yAxisGroup
    .selectAll('rect.bg')
    .data(bands)
    .join('rect')
    .attr('class', (band) => clsx('bg', band.label))
    .attr('x', 1.5)
    .attr('y', (band) => scaleY(band.max) - 2)
    .attr('width', () => getPlotAreaRight(options) - getPlotAreaLeft(options))
    .attr('height', (band) => scaleY(band.min) - scaleY(band.max) + 2);
}

export default function ItemPerformanceChart(props: Props): ReactElement {
  const {
    questions,
    area,
    minPercentageCorrect,
    minItemScaleScore,
    maxItemScaleScore,
    iconWidth,
    itemSorting,
    onClickCategory
  } = props;

  // find unique categories applied to the items
  const categories = useCategoriesFromItems(questions);
  const paddedItems = useMemo<TestItemOrPadding[]>(
    () => [
      ...questions,
      ...categories.map((c) => ({
        id: `${c.id}-padding`,
        categories: [c],
        isPadding: true as const
      }))
    ],
    [categories, questions]
  );

  const itemsByCategory = useMemo<ItemsByCategory>(
    () => groupBy(paddedItems, (i) => getCategory(i.categories)?.id),
    [paddedItems]
  );

  const sortedTestItems = useMemo(
    () =>
      orderBy(
        paddedItems,
        [
          // sort by category (effectively grouping them)
          (item): number => getCategory(item.categories).order,
          (item): number => getCategory(item.categories).id,
          // sort by applied sorting type
          (item): number => {
            if ('isPadding' in item) return itemSorting.asc ? -Infinity : Infinity; // ensure padding item is always first
            if (itemSorting.type === 'encounters') return item.numResponses;
            if (itemSorting.type === 'percentageCorrect') return +item.percentageCorrect;
            if (itemSorting.type === 'difficulty') return item.scaleScore;
            return item.id;
          }
        ],
        ['asc', 'asc', itemSorting.asc ? 'asc' : 'desc']
      ),
    [itemSorting.asc, itemSorting.type, paddedItems]
  );

  const bands = useMemo(
    () =>
      generateBands(
        Math.floor(minItemScaleScore / BAND_INCREMENTS) * BAND_INCREMENTS,
        Math.ceil(maxItemScaleScore / BAND_INCREMENTS) * BAND_INCREMENTS
      ),
    [maxItemScaleScore, minItemScaleScore]
  );

  const scaleX = scaleLinear()
    .domain([0, paddedItems.length])
    .range([getPlotAreaLeft(options), getPlotAreaRight(options)]);

  const scaleY = scaleLinear()
    .domain([bands[0].min, bands[bands.length - 1].max])
    .range([getPlotAreaBottom(options), getPlotAreaTop(options)]);

  return (
    <svg className="bubble-chart" viewBox={`0 0 ${options.size.width} ${options.size.height}`}>
      <g className="axis">
        <g
          className="y-axis"
          ref={(node): void => yAxis({ bands, node, scaleY })}
          transform={`translate(${yAxisPosition(options)}, 0)`}
        />
        <g
          className="x-axis"
          ref={(node): void =>
            xAxis({ categories, itemsByCategory, node, scaleX, onClickCategory })
          }
          transform={`translate(0 ${xAxisPosition(options)})`}
        />
      </g>
      <g className="items">
        {sortedTestItems.map((item, i) => {
          if ('isPadding' in item) return null;
          return (
            <g
              data-pos={i}
              id={`${item.id}`}
              key={`item-${item.id}`}
              transform={`translate(${scaleX(i)} ${scaleY(item.scaleScore)})`}
              tabIndex={0}
              role="button"
            >
              <circle r={iconWidth / 2} fill="white" />
              <FontAwesomeIcon
                icon={minPercentageCorrect <= +item.percentageCorrect ? faCircleUp : faCircleDown}
                color={minPercentageCorrect <= +item.percentageCorrect ? 'green' : 'red'}
                width={iconWidth}
                height={iconWidth}
                x={-iconWidth / 2}
                y={-iconWidth / 2}
              />
              <ItemWithStatsTooltip item={item} area={area} />
            </g>
          );
        })}
      </g>
    </svg>
  );
}
